import { useEffect, useRef } from "preact/hooks";

import { MessagePayload } from "../../components/checkoutForm/services";
import { Config, getErrorMsgAndCode } from "../../services/api";
import { useSDKContext } from "../SDKContext";
import { SDKErrorType } from "./types";

type UseHeadlessSDKApiHandlerOptions = {
  disableErrorFormatting?: boolean;
};

export const useHeadlessSDKApiHandler = (
  apiName: string,
  callback: (data: Record<string, unknown>) => Promise<unknown>,
  options?: UseHeadlessSDKApiHandlerOptions
) => {
  const { messagingService } = useSDKContext();

  const { disableErrorFormatting } = options || {};

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const handleReceive = (
      event: MessageEvent<MessagePayload>,
      reply: (message: MessagePayload) => void
    ) => {
      const { type, data, intentSecret } = event.data;
      if (type !== apiName) return;

      // Forward the secret the JS SDK sends with each message to the BE as the
      // `x-mh-intent-secret` header. Centralized here so every request uses it
      if (intentSecret) Config.intentSecret = intentSecret;

      callbackRef
        .current(data)
        .then((payload) => {
          reply({
            type: apiName,
            data: {
              status: "resolved",
              payload,
            },
          });
        })
        .catch(async (error) => {
          const errorType: SDKErrorType = error?.isHttpError
            ? "network"
            : "unknown";

          if (disableErrorFormatting) {
            reply({
              type: apiName,
              data: {
                status: "rejected",
                payload: { type: errorType, ...(await error.json) },
              },
            });
          } else {
            getErrorMsgAndCode(error).then(({ code, msg, field, errors }) => {
              reply({
                type: apiName,
                data: {
                  status: "rejected",
                  payload: {
                    code,
                    message: msg || error.message,
                    type: errorType,
                    field,
                    errors,
                  },
                },
              });
            });
          }
        });
    };

    messagingService.onReceive(handleReceive);
    return () => {
      messagingService.removeListener(handleReceive);
    };
  }, [apiName, messagingService, disableErrorFormatting]);
};
